import { useId, useState } from 'react';

export type FilterValues = Readonly<Record<string, string>>;

/**
 * A list's filters (DESIGN_SYSTEM.md §13): the values applied to the list, and a draft edited in the filter panel.
 * Opening the panel starts the draft from what is applied; *Apply filters* applies it and closes the panel; *Reset
 * filters* goes back to the defaults at once. `defaults` are read once (e.g. today's date for transactions).
 */
export function useFilters<T extends FilterValues>(defaults: T, onApplied?: () => void) {
  const [initial] = useState(defaults);
  const [open, setOpen] = useState(false);
  const [applied, setApplied] = useState<T>(initial);
  const [draft, setDraft] = useState<T>(initial);
  const panelId = `filters-${useId()}`;
  const differs = (a: T, b: T) => Object.keys({ ...a, ...b }).some((k) => (a[k] ?? '') !== (b[k] ?? ''));

  return {
    open,
    panelId,
    applied,
    draft,
    setDraft,
    set: (key: keyof T & string, value: string) => setDraft((d) => ({ ...d, [key]: value })),
    /** Filters that differ from their defaults, for the button's count. */
    count: Object.keys(initial).filter((k) => (applied[k] ?? '') !== (initial[k] ?? '')).length,
    /** The draft has changes to apply. */
    dirty: differs(draft, applied),
    /** Anything differs from the defaults, applied or not. */
    changed: differs(draft, initial) || differs(applied, initial),
    toggle: () => {
      if (!open) setDraft(applied);
      setOpen(!open);
    },
    close: () => setOpen(false),
    apply: () => {
      setApplied(draft);
      setOpen(false);
      onApplied?.();
    },
    /** Applies these values at once, e.g. a quick filter; filters not named go back to their defaults. */
    applyValues: (values: Partial<T>) => {
      const next = { ...initial, ...values } as T;
      setDraft(next);
      setApplied(next);
      onApplied?.();
    },
    reset: () => {
      setDraft(initial);
      setApplied(initial);
      onApplied?.();
    },
  };
}

/** What the filter button and panel need from {@link useFilters}. */
export type ListFilters = Pick<
  ReturnType<typeof useFilters<FilterValues>>,
  'open' | 'panelId' | 'count' | 'dirty' | 'changed' | 'toggle' | 'close' | 'apply' | 'reset'
>;

/** Only the filters that have a value go in the query. */
export const withValues = (values: FilterValues) => Object.fromEntries(Object.entries(values).filter(([, v]) => v));
